import { buildApiUrl } from "../../../config.js";
import React, { useState, useEffect } from "react";
import { HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import EditModal from "./EditModal";
import AddItemModal from "./AddItemModal";
import ImageModal from "./ImageModal";
import ExportToExcel from "./ExportToExcel";
import { capitalizeWords } from "../../ui/capitalizeWords";
import { ColorRing } from "react-loader-spinner";
import { FaPlus } from "react-icons/fa";

const CategoriesTable = () => {
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  useEffect(() => {
    fetchCategories();
  }, []);

  const userRole = localStorage.getItem("role");

  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(buildApiUrl("/api/categories"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (Array.isArray(data)) {
        // Sort by createdAt descending (newest first) in case backend doesn't sort
        const sortedData = data.sort((a, b) => {
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB - dateA; // Descending order (newest first)
        });
        setCategories(sortedData);
      } else {
        setError("Failed to fetch categories");
      }
    } catch (error) {
      setError("An error occurred while fetching categories");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const filteredCategories = categories.filter((category) => {
    const normalizedSearchTerm = searchTerm.toLowerCase().trim();
    const categoryName = category.categoryName?.toLowerCase() || "";
    const itemName = category.itemName?.toLowerCase() || "";
    const itemCode = category.itemCode?.toLowerCase() || "";
    const unit = category.unit?.toLowerCase() || "";
    const pcsPerCtn = category.pcsPerCtn?.toString().toLowerCase() || "";
    const wtPerCtn = category.wtPerCtn?.toString().toLowerCase() || "";
    const cbmPerCtn = category.cbmPerCtn?.toString().toLowerCase() || "";
    return (
      categoryName.includes(normalizedSearchTerm) ||
      itemName.includes(normalizedSearchTerm) ||
      itemCode.includes(normalizedSearchTerm) ||
      unit.includes(normalizedSearchTerm) ||
      pcsPerCtn.includes(normalizedSearchTerm) ||
      wtPerCtn.includes(normalizedSearchTerm) ||
      cbmPerCtn.includes(normalizedSearchTerm)
    );
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCategories.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setIsModalOpen(true);
  };

  const handleAddItems = (items) => {
    // Add new items at the beginning and sort by createdAt (newest first)
    setCategories((prevCategories) => {
      const combined = [...items, ...prevCategories];
      return combined.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA; // Descending order (newest first)
      });
    });
  };

  const handleUpdateCategory = (updatedCategory) => {
    setCategories((prevCategories) =>
      prevCategories.map((category) =>
        category._id === updatedCategory._id ? updatedCategory : category
      )
    );
    setIsModalOpen(false);
  };

  const handleDelete = async (categoryId) => {
    if (!categoryId) {
      toast.error("Category ID is missing.");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        buildApiUrl(`/api/categories/${categoryId}`),
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        toast.success("Category deleted successfully!");
        setCategories((prevCategories) =>
          prevCategories.filter((category) => category._id !== categoryId)
        );
      } else {
        const data = await response.json();
        toast.error(data.message || "Failed to delete category.");
      }
    } catch (error) {
      toast.error("An error occurred while deleting the category.");
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCategory(null);
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage("");
  };

  return (
    <div className="bg-white">
      <div className="">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-2 gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between sm:space-x-4 w-full">
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Search Categories..."
              className="px-2 py-1 border border-gray-300 bg-gray-50 rounded-md shadow-sm w-full sm:w-1/3 focus:outline-none focus:ring-2 focus:ring-blue-700"
            />
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-fit mt-2 sm:mt-0">
              <button
                onClick={() => setIsAddItemModalOpen(true)}
                className="bg-indigo-600 flex gap-1 justify-center items-center text-white px-3 py-2 text-xs font-medium w-full sm:w-fit rounded-md shadow-lg hover:bg-indigo-800"
              >
                <FaPlus size={12} />
                Add Item
              </button>
              <ExportToExcel data={filteredCategories} fileName="Categories" />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center">
            <ColorRing
              visible={true}
              height="80"
              width="80"
              ariaLabel="color-ring-loading"
              wrapperStyle={{}}
              wrapperClass="color-ring-wrapper"
              colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
            />
          </div>
        ) : error ? (
          <div className="text-center py-4 text-red-500">
            <p>{error}</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-w-[100vw] rounded-md border shadow-md border-gray-300 max-h-[40rem]">
            <table className="min-w-full table-auto rounded-lg overflow-scroll shadow-md border-spacing-0 border-collapse">
              <thead className="bg-gray-700 text-white sticky top-0 z-10">
                <tr>
                  <th className="p-2 text-left font-normal tracking-wide">#</th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Item Code
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Category
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Item Name
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Unit
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Image
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Qty/Ctn
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Wt/Ctn
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    CBM/Ctn
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {currentItems.length > 0 ? (
                  currentItems.map((category, index) => (
                    <tr
                      key={category._id}
                      className="hover:bg-gray-100 border divide-x text-gray-700 divide-gray-200 border-b border-collapse border-gray-200 transition-colors duration-200 cursor-pointer"
                    >
                      <td className="px-2 py-0 5">
                        {indexOfFirstItem + index + 1}
                      </td>
                      <td className="px-2 py-0.5 font-semibold">
                        {capitalizeWords(category.itemCode)}
                      </td>
                      <td className="px-2 py-0.5">
                        {capitalizeWords(category.categoryName)}
                      </td>
                      <td className="px-2 py-0.5 font-semibold">
                        {capitalizeWords(category.itemName)}
                      </td>
                      <td className="px-2 py-0.5">{category.unit}</td>
                      <td className="px-2 py-0.5">
                        {category.itemImage ? (
                          category.itemImage.includes("drive.google.com") ? (
                            <img
                              src={`https://drive.google.com/thumbnail?id=${
                                category.itemImage.split("id=")[1]
                              }`}
                              alt="Item"
                              className="size-4 object-cover rounded-full"
                              onClick={() =>
                                handleImageClick(
                                  `https://drive.google.com/thumbnail?id=${
                                    category.itemImage.split("id=")[1]
                                  }`
                                )
                              }
                            />
                          ) : (
                            <img
                              src={category.itemImage}
                              alt="Item"
                              className="size-4 object-cover rounded-full"
                              onClick={() =>
                                handleImageClick(category.itemImage)
                              }
                            />
                          )
                        ) : (
                          <span>No image available</span>
                        )}
                      </td>
                      <td className="px-2 py-0.5">
                        {category.pcsPerCtn || "N/A"}
                      </td>
                      <td className="px-2 py-0.5">
                        {category.wtPerCtn || "N/A"}
                      </td>
                      <td className="px-2 py-0.5">
                        {category.cbmPerCtn || "N/A"}
                      </td>
                      <td className="px-2 py-0.5 text-sm text-center space-x-2">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(category)}
                            className="text-blue-500 hover:text-blue-700"
                          >
                            <HiOutlinePencil size={16} />
                          </button>
                          {userRole === "admin" && (
                            <button
                              onClick={() => handleDelete(category._id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <HiOutlineTrash size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-6 py-4 text-center text-gray-500"
                    >
                      No categories found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex justify-between items-center">
          <span className="text-sm text-gray-500">
            Showing {indexOfFirstItem + 1} to{" "}
            {Math.min(indexOfLastItem, filteredCategories.length)} of{" "}
            {filteredCategories.length} categories
          </span>
          <div className="flex space-x-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-xs font-medium bg-gray-200 text-gray-600 rounded-lg"
            >
              Prev
            </button>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-xs font-medium bg-gray-200 text-gray-600 rounded-lg"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {isImageModalOpen && (
        <ImageModal imageUrl={selectedImage} onClose={closeImageModal} />
      )}

      <ToastContainer />

      {isAddItemModalOpen && (
        <AddItemModal
          onClose={() => setIsAddItemModalOpen(false)}
          onAddItems={handleAddItems}
        />
      )}

      {isModalOpen && (
        <EditModal
          category={selectedCategory}
          onClose={closeModal}
          onUpdate={handleUpdateCategory}
        />
      )}
    </div>
  );
};

export default React.memo(CategoriesTable);
